import { useCustomEventsQuery } from 'api/MappingsAPI';
import { useCurrentProject } from 'api/ProjectsAPI';
import { selectFeatureFlags } from 'core/userContext';
import { debounce } from 'lodash';
import {
  convertMappingToMergedCustomEvent,
  formatWorkflowTriggers,
} from 'modules/customEvents/utils';
import { ReactNode, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomEventsRequest, fetchDeletionPoliciesRequest } from '../actions';
import { DEBOUNCE_TIMEOUTS_MS } from '../constants';
import {
  selectEvents,
  selectEventsByName,
  selectIsErrorEvents,
  selectIsLoadingEvents,
  selectListReferenceCountsByName,
  selectRetentionPeriodsByName,
  selectWorkflowReferenceCountsByName,
} from '../selectors';
import { CustomEvent, CustomEventPermission, Project } from '../types';
import { CustomEventsQueryContext } from './CustomEventsQueryContext';

const defaultEvent = {
  lastSeen: 0,
  docCount: 0,
  references: 0,
  workflowReferences: 0,
  listReferences: 0,
  retentionPolicy: -1,
};
const defaultPermission = { allowed: true, saved: true };

export const CustomEventsQueryContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { SchemaManagementUI } = useSelector(selectFeatureFlags);

  /**
   * Project
   */
  const {
    data: project = {} as Project,
    refetch: refetchProject,
    isLoading: isLoadingProjectSettings,
  } = useCurrentProject({
    refetchOnWindowFocus: false,
  });

  const {
    extendedProjectSettings: { allowNewCustomEvents = false, customEventPermissions = [] } = {},
  } = project;

  /**
   * CustomEvents from JoinedEventData
   * Deletion Policies for retention periods
   */
  useEffect(() => {
    dispatch(fetchCustomEventsRequest('', dispatch));
    dispatch(fetchDeletionPoliciesRequest());
  }, []);

  /**
   * Custom event mappings
   */
  const {
    data: customEventMappings = [],
    isError: isErrorCustomEventMappings,
    isLoading: isLoadingCustomEventMappings,
    refetch,
  } = useCustomEventsQuery({ fetchLatest: true });

  /**
   * Formatted values
   */
  const isLoadingEvents = useSelector(selectIsLoadingEvents);
  const isErrorEvents = useSelector(selectIsErrorEvents);
  const joinedData = useSelector(selectEvents);
  const eventsByName = useSelector(selectEventsByName);
  const workflowReferenceCountsByName = useSelector(selectWorkflowReferenceCountsByName);
  const listReferenceCountsByName = useSelector(selectListReferenceCountsByName);
  const retentionPeriodsByName = useSelector(selectRetentionPeriodsByName);

  /**
   It is possible for an event to have been configured via project settings,
   without ever yet having occurred (ie. been registered in ElasticSearch).
   In that case, we still want to show the event on the CustomEvent[] table
   so that users can configure event-specific settings (discarded/saved/deactivated).
   Therefore we must show any events that occur in either the ES data
   (selectEvents) OR the project settings data (selectCustomEventsPermissions).
  */
  // merge joinedEventData with project.json for complete list of events
  const eventNames = [
    ...new Set([
      // joinedEventData
      ...Object.keys(eventsByName),
      // project.json
      ...customEventPermissions.map((permission: CustomEventPermission) => permission.name),
    ]),
  ];

  const customEventPermissionsByEventName = customEventPermissions.reduce(
    (
      acc: Record<string, { allowed: boolean; saved: boolean; eventName: string }>,
      { allowed, saved, name }: CustomEventPermission,
    ) => {
      acc[name] = { eventName: name, allowed, saved };
      return acc;
    },
    {},
  );

  const joinedEventData = useMemo(() => {
    return joinedData.reduce((acc: Record<string, CustomEvent>, curr: CustomEvent) => {
      Object.assign(acc, { [curr.eventName]: curr });
      return acc;
    }, {});
  }, [joinedData]);

  const customEvents = SchemaManagementUI
    ? customEventMappings.map((event) =>
        convertMappingToMergedCustomEvent(event, joinedEventData, retentionPeriodsByName),
      )
    : eventNames.map((eventName: string) => {
        const event = eventsByName[eventName] || defaultEvent;
        const permission = customEventPermissionsByEventName[eventName] || defaultPermission;
        const workflowReferences = workflowReferenceCountsByName[eventName] || 0;
        const listReferences = listReferenceCountsByName[eventName] || 0;
        const retentionPolicy = retentionPeriodsByName[eventName] || -1;
        const workflowTriggers = formatWorkflowTriggers(event?.workflowAndNodes);

        /**
         * In cases where permissions have not yet been explicitly configured,
         * allowed and saved should default to true. This reflects the default
         * ingestion policy, which is to accept events.
         */
        return {
          ...event,
          ...permission,
          workflowReferences,
          workflowTriggers,
          listReferences,
          references: workflowReferences + listReferences,
          retentionPolicy,
        };
      });

  const refetchCustomEventMappings = debounce(() => refetch(), DEBOUNCE_TIMEOUTS_MS.fetchMappings);

  return (
    <CustomEventsQueryContext.Provider
      value={{
        customEvents,
        eventsByName,
        isErrorCustomEvents: SchemaManagementUI ? isErrorCustomEventMappings : isErrorEvents,
        isLoadingCustomEvents: SchemaManagementUI ? isLoadingCustomEventMappings : isLoadingEvents,
        project,
        refetchCustomEventMappings,
        refetchProject,
        allowNewCustomEvents,
        customEventPermissions,
        isLoadingProjectSettings,
      }}
    >
      {children}
    </CustomEventsQueryContext.Provider>
  );
};
